/**
 * Client preferences management utilities
 * These functions help manage user preferences that need to be available during SSR
 */

import { SRP_HITS_PER_PAGE, isValidSrpHitsPerPage } from '../srp/srp-hits-per-page';
import { getQueryClient } from '../../query/queryClient';
import { userStateQueryKey } from '../../query/user-state/userStateQueryKeys';
import { extractJobsFromUserState } from '../user-state/extract-jobs-from-user-state';
import type { UserStateMap } from '../../types/user-state';
import type { JobFormState } from '../../redux/features/jobFormSlice';
import type { ComparisonPreferencesState } from '../../redux/features/comparisonPreferencesSlice';

export type SortPreference = 'relevance' | 'date' | 'salary';

export interface ClientPreferences {
  theme: 'light' | 'dark' | 'system';
  sort: SortPreference;
  isFinland: boolean;
  /** Remembered SRP results-per-page choice (see srp-hits-per-page.ts). */
  perPage: number;
  // Optional: job state snapshots for SSR parity (IDs only)
  savedJobs?: string[];
  appliedJobs?: string[];
  viewedJobs?: string[];
}

/**
 * Get client preferences from cookies during SSR
 */
export function getClientPreferencesFromCookies(cookieHeader: string | null): Partial<ClientPreferences> {
  if (!cookieHeader) return {};
  
  const preferences: Partial<ClientPreferences> = {};
  
  // Parse theme cookie
  const themeMatch = cookieHeader.match(/(?:^|;\s*)theme=([^;]+)/);
  if (themeMatch) {
    const theme = decodeURIComponent(themeMatch[1]);
    if (['light', 'dark', 'system'].includes(theme)) {
      preferences.theme = theme as 'light' | 'dark' | 'system';
    }
  }
  
  // Parse sort cookie
  const sortMatch = cookieHeader.match(/(?:^|;\s*)sort=([^;]+)/);
  if (sortMatch) {
    const sort = decodeURIComponent(sortMatch[1]);
    if (['relevance', 'date', 'salary'].includes(sort)) {
      preferences.sort = sort as SortPreference;
    }
  }
  
  // Parse isFinland cookie
  const isFinlandMatch = cookieHeader.match(/(?:^|;\s*)isFinland=([^;]+)/);
  if (isFinlandMatch) {
    const isFinland = decodeURIComponent(isFinlandMatch[1]);
    preferences.isFinland = isFinland === 'true';
  }

  // Parse perPage cookie
  const perPageMatch = cookieHeader.match(/(?:^|;\s*)perPage=([^;]+)/);
  if (perPageMatch) {
    const perPage = parseInt(decodeURIComponent(perPageMatch[1]), 10);
    if (isValidSrpHitsPerPage(perPage)) {
      preferences.perPage = perPage;
    }
  }

  // Parse job state cookies (JSON arrays of IDs)
  const jobKeys = ['savedJobs', 'appliedJobs', 'viewedJobs'] as const;
  for (const key of jobKeys) {
    const m = cookieHeader.match(new RegExp(`(?:^|;\\s*)${key}=([^;]+)`));
    if (m) {
      try {
        const raw = decodeURIComponent(m[1]);
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) (preferences as any)[key] = arr.slice(0, 500).map(String);
      } catch {}
    }
  }
  
  return preferences;
}

/**
 * Get default client preferences
 */
export function getDefaultClientPreferences(): ClientPreferences {
  return {
    theme: 'system',
    sort: 'relevance',
    isFinland: true,  // Default to true to match Finland filter slice
    perPage: SRP_HITS_PER_PAGE,
  };
}

/**
 * Get effective client preferences (merged with defaults)
 */
export function getEffectiveClientPreferences(cookieHeader: string | null): ClientPreferences {
  const cookiePrefs = getClientPreferencesFromCookies(cookieHeader);
  const defaults = getDefaultClientPreferences();
  
  return {
    ...defaults,
    ...cookiePrefs
  };
}

/**
 * Set a client preference cookie
 */
export function setClientPreferenceCookie(name: string, value: string): void {
  if (typeof document === 'undefined') return;
  
  const oneYearInSeconds = 365 * 24 * 60 * 60;
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${oneYearInSeconds}; SameSite=Lax`;
}

/**
 * Get client preferences from localStorage (client-side only)
 */
export function getClientPreferencesFromLocalStorage(): Partial<ClientPreferences> {
  if (typeof window === 'undefined') return {};
  
  const preferences: Partial<ClientPreferences> = {};
  
  const theme = localStorage.getItem('theme');
  if (theme && ['light', 'dark', 'system'].includes(theme)) {
    preferences.theme = theme as 'light' | 'dark' | 'system';
  }
  
  const sort = localStorage.getItem('sort');
  if (sort && ['relevance', 'date', 'salary'].includes(sort)) {
    preferences.sort = sort as SortPreference;
  }
  
  const isFinland = localStorage.getItem('isFinland');
  if (isFinland) {
    preferences.isFinland = isFinland === 'true';
  }

  const perPage = parseInt(localStorage.getItem('perPage') || '', 10);
  if (isValidSrpHitsPerPage(perPage)) {
    preferences.perPage = perPage;
  }
  
  // Job state snapshots (IDs only)
  const jobKeys = ['savedJobs', 'appliedJobs', 'viewedJobs'] as const;
  for (const key of jobKeys) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) continue;
      const ids = parsed.slice(0, 500).map((x: any) => String(typeof x === 'object' && x?.id ? x.id : x));
      (preferences as any)[key] = ids;
    } catch {}
  }
  
  return preferences;
}

/**
 * Sync localStorage preferences to cookies
 */
export function syncPreferencesToCookies(): void {
  if (typeof window === 'undefined') return;
  
  const prefs = getClientPreferencesFromLocalStorage();
  
  if (prefs.theme) setClientPreferenceCookie('theme', prefs.theme);
  if (prefs.sort) setClientPreferenceCookie('sort', prefs.sort);
  if (prefs.isFinland !== undefined) setClientPreferenceCookie('isFinland', prefs.isFinland.toString());
  if (prefs.perPage !== undefined) setClientPreferenceCookie('perPage', prefs.perPage.toString());

  // Mirror job state arrays into cookies (IDs only, capped) so SSR can compute isSaved/match-score
  // for anonymous visitors too — see `finalSavedJobIds` in pages/index/+data.ts, which already reads
  // these cookies as its fallback when there's no signed-in userState. Source of truth is the
  // TanStack `userState` query cache (query/user-state/*), not localStorage — this used to read
  // plain localStorage keys that predate the TEC-404 migration and nothing has written since.
  try {
    const userState = getQueryClient().getQueryData<UserStateMap>(userStateQueryKey);
    if (userState) {
      const jobsByKey = extractJobsFromUserState(userState);
      for (const key of ['savedJobs', 'appliedJobs', 'viewedJobs'] as const) {
        const ids = jobsByKey[key].slice(0, 500).map((job) => job.id);
        setClientPreferenceCookie(key, JSON.stringify(ids));
      }
    }
  } catch {}
}

/** Only the `currentJob`/`desiredJob`/weight fields calculateJobMatchScore actually reads — never
 *  the CV/company/description fields, which are both irrelevant to scoring and unnecessarily
 *  large/sensitive for a cookie. */
export interface CookieMatchProfile {
  currentJob?: {
    title?: string;
    normalisedTitle?: string;
    normalisedRoleLevel?: string;
    salary?: number;
    workPercentage?: number;
    locations?: string[];
    remoteness?: string;
    jobType?: string;
    benefits?: string[];
    vacationWeeks?: number;
    languages?: string[];
  };
  desiredJob?: {
    titles?: string[];
    normalisedRoleLevels?: string[];
    locations?: string[];
    remoteness?: string[];
    jobTypes?: string[];
    salaryMin?: number;
  };
  weights?: Partial<ComparisonPreferencesState>;
}

/** Cap on how many entries an array field keeps when mirrored into the match-profile cookie. */
const MATCH_PROFILE_ARRAY_CAP = 20;
/** Skip writing the cookie rather than risk exceeding browsers' ~4KB per-cookie limit. */
const MATCH_PROFILE_COOKIE_MAX_CHARS = 3000;

function capStrings(value: string[] | undefined): string[] | undefined {
  if (!Array.isArray(value) || value.length === 0) return undefined;
  return value.slice(0, MATCH_PROFILE_ARRAY_CAP).map(String);
}

/**
 * Trims a full `JobFormState`/`ComparisonPreferencesState` down to the minimal
 * {@link CookieMatchProfile} shape and mirrors it into a cookie, so anonymous visitors' match
 * score can be computed during SSR the same way `resolveServerJobFormProfile`/
 * `resolveServerComparisonWeights` (utils/job-match/resolve-ssr-profile-and-weights.ts) already do
 * for signed-in users from `userState.profile.data`/`.preferences.data`. `jobForm`/
 * `comparisonPreferences` are redux-persist/IndexedDB-only, unreachable during SSR, which is why
 * this cookie exists — see `getMatchProfileFromCookies` for the read side and its call site in
 * pages/index/+data.ts.
 */
export function syncMatchProfileToCookies(jobForm: JobFormState, weights: ComparisonPreferencesState): void {
  if (typeof document === 'undefined') return;
  try {
    const currentJob = jobForm.currentJob;
    const hasCurrentJob = Boolean(currentJob && (currentJob.salary || currentJob.title || currentJob.normalisedTitle));
    const profile: CookieMatchProfile = {
      ...(hasCurrentJob
        ? {
            currentJob: {
              title: currentJob.title || undefined,
              normalisedTitle: currentJob.normalisedTitle || undefined,
              normalisedRoleLevel: currentJob.normalisedRoleLevel || undefined,
              salary: typeof currentJob.salary === 'number' ? currentJob.salary : undefined,
              workPercentage: typeof currentJob.workPercentage === 'number' ? currentJob.workPercentage : undefined,
              locations: capStrings(currentJob.locations),
              remoteness: currentJob.remoteness || undefined,
              jobType: currentJob.jobType || undefined,
              benefits: capStrings(currentJob.benefits),
              vacationWeeks: typeof currentJob.vacationWeeks === 'number' ? currentJob.vacationWeeks : undefined,
              languages: capStrings(currentJob.languages),
            },
          }
        : {}),
      desiredJob: {
        titles: capStrings(jobForm.desiredJob?.titles),
        normalisedRoleLevels: capStrings(jobForm.desiredJob?.normalisedRoleLevels),
        regionTags: capStrings(jobForm.desiredJob?.regionTags),
        locations: capStrings(jobForm.desiredJob?.locations),
        remoteness: capStrings(jobForm.desiredJob?.remoteness),
        jobTypes: capStrings(jobForm.desiredJob?.jobTypes),
        salaryMin: typeof jobForm.desiredJob?.salaryMin === 'number' ? jobForm.desiredJob.salaryMin : undefined,
      },
      weights,
    };

    if (!hasCurrentJob) {
      // No point mirroring desiredJob/weights alone — calculateJobMatchScore returns null without
      // a currentJob, and an empty cookie just wastes request bytes on every page load.
      return;
    }

    const serialized = JSON.stringify(profile);
    if (serialized.length > MATCH_PROFILE_COOKIE_MAX_CHARS) return;
    setClientPreferenceCookie('matchProfile', serialized);
  } catch {}
}

/**
 * Reads the anonymous-visitor match-profile cookie written by {@link syncMatchProfileToCookies}.
 * Server-side counterpart — see its call site in pages/index/+data.ts.
 */
export function getMatchProfileFromCookies(cookieHeader: string | null): CookieMatchProfile | null {
  if (!cookieHeader) return null;
  const m = cookieHeader.match(/(?:^|;\s*)matchProfile=([^;]+)/);
  if (!m) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(m[1]));
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed as CookieMatchProfile;
  } catch {
    return null;
  }
}
